import { useMemo, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { Card, Button } from "@/design-system/components/ui";
import { Table, THead, TBody, TR, TH, TD } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { spvPoolsApi } from "@/api/spvPools";
import {
  useInvestorCommitments,
  useInvestorDistributions,
} from "@/hooks/capitalMarkets/useInvestors";
import { useSpvPoolList } from "@/hooks/capitalMarkets/useSpvPools";
import { downloadCsv, formatCurrency, formatDate } from "../../shared/format";

export function DistributionsTab({ investorId }: { investorId: string }) {
  const [year, setYear] = useState("");

  const commitmentsQuery = useInvestorCommitments({ investorId, pageSize: 200 });
  const distributionsQuery = useInvestorDistributions({ pageSize: 500 });
  const poolsQuery = useSpvPoolList({ pageSize: 200 });

  const commitments = useMemo(
    () => commitmentsQuery.data?.data ?? [],
    [commitmentsQuery.data],
  );
  const commitmentIds = useMemo(
    () => new Set(commitments.map((c) => c.id)),
    [commitments],
  );
  const commitmentPool = useMemo(() => {
    const m = new Map<string, { poolId: string; trancheId: string }>();
    for (const c of commitments)
      m.set(c.id, { poolId: c.spvPoolId, trancheId: c.poolTrancheId });
    return m;
  }, [commitments]);

  const poolName = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of poolsQuery.data?.data ?? []) m.set(p.id, p.poolName);
    return m;
  }, [poolsQuery.data]);

  const poolIds = useMemo(
    () => [...new Set(commitments.map((c) => c.spvPoolId))],
    [commitments],
  );
  const mathQueries = useQueries({
    queries: poolIds.map((id) => ({
      queryKey: ["spv-pools", "math", id],
      queryFn: () => spvPoolsApi.math(id),
    })),
  });
  const trancheCode = useMemo(() => {
    const m = new Map<string, string>();
    for (const q of mathQueries) {
      for (const t of q.data?.perTrancheFill ?? []) {
        m.set(t.poolTrancheId, t.trancheCode);
      }
    }
    return m;
  }, [mathQueries]);

  const distributions = useMemo(
    () =>
      (distributionsQuery.data?.data ?? []).filter((d) =>
        commitmentIds.has(d.investorCommitmentId),
      ),
    [distributionsQuery.data, commitmentIds],
  );

  const years = useMemo(() => {
    const set = new Set<string>();
    for (const d of distributions) set.add(d.periodEnd.slice(0, 4));
    return [...set].sort().reverse();
  }, [distributions]);

  const rows = useMemo(
    () =>
      year
        ? distributions.filter((d) => d.periodEnd.slice(0, 4) === year)
        : distributions,
    [distributions, year],
  );

  const handleExport = () => {
    const header = [
      "Year",
      "Period Start",
      "Period End",
      "Gross",
      "Withheld",
      "Net",
      "Paid At",
      "Pool",
      "Tranche",
    ];
    // Grouped by year (ascending) for K-1 / 1099 preparation.
    const sorted = [...rows].sort((a, b) =>
      a.periodEnd.localeCompare(b.periodEnd),
    );
    const csv = sorted.map((d) => {
      const link = commitmentPool.get(d.investorCommitmentId);
      return [
        d.periodEnd.slice(0, 4),
        d.periodStart,
        d.periodEnd,
        String(d.grossAmount),
        String(d.withheldAmount),
        String(d.netAmount),
        d.paidAt ?? "",
        link ? (poolName.get(link.poolId) ?? "") : "",
        link ? (trancheCode.get(link.trancheId) ?? "") : "",
      ];
    });
    downloadCsv(
      `investor-distributions-${year || "all"}.csv`,
      [header, ...csv],
    );
  };

  if (distributionsQuery.isLoading || commitmentsQuery.isLoading) {
    return (
      <Card>
        <p className="text-small text-gray-mid italic m-0">
          Loading distributions…
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-space-3 mb-space-3">
        <div className="flex items-center gap-space-3">
          <h3 className="font-display font-bold text-h3 m-0">Distributions</h3>
          <Select
            aria-label="Filter by year"
            className="w-32"
            value={year}
            onChange={(e) => setYear(e.target.value)}
          >
            <option value="">All years</option>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </Select>
        </div>
        <Button variant="secondary" size="sm" onClick={handleExport}>
          <Download size={14} strokeWidth={1.5} aria-hidden /> Export CSV
        </Button>
      </div>
      {rows.length === 0 ? (
        <p className="text-small text-gray-mid italic m-0">
          No distributions recorded for this investor yet.
        </p>
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>Period Start</TH>
              <TH>Period End</TH>
              <TH>Gross</TH>
              <TH>Withheld</TH>
              <TH>Net</TH>
              <TH>Paid At</TH>
              <TH>Pool</TH>
              <TH>Tranche</TH>
            </TR>
          </THead>
          <TBody>
            {rows.map((d) => {
              const link = commitmentPool.get(d.investorCommitmentId);
              return (
                <TR key={d.id}>
                  <TD>{formatDate(d.periodStart)}</TD>
                  <TD>{formatDate(d.periodEnd)}</TD>
                  <TD>{formatCurrency(d.grossAmount)}</TD>
                  <TD>{formatCurrency(d.withheldAmount)}</TD>
                  <TD>{formatCurrency(d.netAmount)}</TD>
                  <TD>{formatDate(d.paidAt)}</TD>
                  <TD>{link ? (poolName.get(link.poolId) ?? "—") : "—"}</TD>
                  <TD>{link ? (trancheCode.get(link.trancheId) ?? "—") : "—"}</TD>
                </TR>
              );
            })}
          </TBody>
        </Table>
      )}
    </Card>
  );
}
